import {
	resolveExternalGeometrySourceDescriptorKey,
	resolveTopologyEndpointAnchorId,
	resolveTopologyFaceReferenceSourceKey,
	resolveTopologySourceMemberScopeKey,
} from '../model/ExternalGeometrySourceDescriptor';
import {
	createExecutionDiagnostic,
	type ExecutionDiagnostic,
} from '../model/SketchEntityReferenceDiagnostics';
import {
	isActiveSketchEntityReferenceRecord,
	isRetainedSketchEntityReferenceRecord,
	resolveSketchEntityReferenceBoundaryMemberState,
	type SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import type {
	TopologyEdgeRelocationCandidate,
} from '../source/topology/TopologyEdgeRelocationService';
import { TopologyFaceBoundaryRuntimeService } from '../source/topology/TopologyFaceBoundaryRuntimeService';
import { TopologyEdgeSourceDescriptorFactory } from '../source/topology/TopologyEdgeSourceDescriptorFactory';
import { TopologyFaceRegionBoundaryResolver } from '../source/topology/TopologyFaceRegionBoundaryResolver';
import {SketchReferenceTargetAnchorBindingCollector} from '../relation/SketchReferenceTargetAnchorBindingCollector';
import {SketchReferenceTargetEndpointCanonicalizer} from '../target/SketchReferenceTargetEndpointCanonicalizer';
import { TopologyBoundaryMemberCorrespondenceResolver } from './TopologyBoundaryMemberCorrespondenceResolver';
import {TopologyEndpointAnchorEvidenceResolver} from './TopologyEndpointAnchorEvidenceResolver';
import type {SketchEntityReferenceRepositoryWriteScope} from '../repository/SketchEntityReferenceRepository';
import type {SketchEntityReferenceRebuildTransactionPort} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRebuildBatchTransaction';
import { TopologyLoopWireMatcher } from './TopologyLoopWireMatcher';
import type {
	TopologyBoundaryMemberMatch,
} from '../../../../types/reference/sketch-entity-reference/TopologyBoundaryMemberCorrespondence';
import type {
	TopologyBoundaryReferenceGroupReconcilerDependencies,
	TopologyBoundaryReferenceGroupReconcileResult,
	TopologyBoundaryReferenceGroupReconcilerPort,
} from '../../../../types/reference/sketch-entity-reference/TopologyBoundaryReferenceGroupReconciler';
import type {
	TopologyFaceBoundaryRuntimeResult,
} from '../../../../types/reference/sketch-entity-reference/TopologyFaceBoundaryRuntime';
import type {
	TopologyFaceBoundaryMemberEvidence,
	TopologyFaceReferenceSourceDescriptor,
} from '../../../../types/reference/sketch-entity-reference/TopologyFaceReferenceSource';
import type {
	SketchReferenceTargetEndpointTopologyAssignment,
	SketchReferenceTargetEndpointTopologyPlan,
} from '../../../../types/reference/sketch-entity-reference/SketchReferenceTargetEndpointTopology';
import {executeSketchEntityReferenceRepositoryWrite} from '../utils/executeSketchEntityReferenceRepositoryWrite';
import {createSketchEntityReferenceIntentBatchKey} from '../utils/createSketchEntityReferenceBatchKey';

/**
 * Convert Entities topology face 动态边成员协调器。
 *
 * 本类只在阶段 B 的目标草图 checkpoint 前运行：先以面为来源确定当前边界，
 * 再把匹配成员交回既有单边重建链路。它不改变普通 topology-edge 引用语义。
 */
export class TopologyBoundaryReferenceGroupReconciler
implements TopologyBoundaryReferenceGroupReconcilerPort {
	/** Face/Wire 运行时边界解析服务。 */
	private readonly faceBoundaryRuntimeService: TopologyFaceBoundaryRuntimeService;
	/** 创建时指定 inner Wire 的唯一匹配器。 */
	private readonly loopWireMatcher = new TopologyLoopWireMatcher();
	/** 当前边候选到持久来源描述的统一工厂。 */
	private readonly edgeDescriptorFactory = new TopologyEdgeSourceDescriptorFactory();
	/** 面组成员与普通边、Loop 共用的身份证据裁决策略。 */
	private readonly memberCorrespondenceResolver =
		new TopologyBoundaryMemberCorrespondenceResolver();
	/** 创建与重建共用的相邻面区域外边界算法。 */
	private readonly faceRegionBoundaryResolver = new TopologyFaceRegionBoundaryResolver();
	/** 统一读取不同目标曲线的正式端点对象。 */
	private readonly targetAnchorBindingCollector =
		new SketchReferenceTargetAnchorBindingCollector();
	/** 判断旧共享点能否继续被当前来源顶点安全复用。 */
	private readonly targetEndpointCanonicalizer =
		new SketchReferenceTargetEndpointCanonicalizer();
	/** 成员匹配与目标端点归属共用的 topology 端点证据。 */
	private readonly endpointAnchorEvidenceResolver =
		new TopologyEndpointAnchorEvidenceResolver();

	/**
	 * @param dependencies checkpoint runtime 与正式创建、删除事务。
	 */
	public constructor(
		private readonly dependencies: TopologyBoundaryReferenceGroupReconcilerDependencies,
	) {
		this.faceBoundaryRuntimeService = new TopologyFaceBoundaryRuntimeService({
			resolveTopologyOwnerHost: descriptor => (
				this.dependencies.runtime.resolveTopologyOwnerHost?.(descriptor) ?? null
			),
			queryTopologyFaceBoundaries: (payload) => {
				const query = this.dependencies.runtime.queryTopologyFaceBoundaries;
				if (!query) {
					throw new Error('当前引用运行时不支持 Convert Entities Face/Wire 查询');
				}
				return query.call(this.dependencies.runtime, payload);
			},
		});
	}

	/**
	 * 协调一批 checkpoint 引用中的 topology face 成员。
	 *
	 * @param references 执行前冻结的目标草图引用。
	 * @param definitionChangeOwnerId 本轮阶段 B 传播 ID。
	 * @param isInputCurrent 当前 checkpoint 是否仍拥有正式状态提交权。
	 * @returns 协调后的 active 引用及成员增删结果。
	 */
	public async reconcile(
		references: readonly SketchEntityReferenceRecord[],
		definitionChangeOwnerId: string,
		isInputCurrent: () => boolean = () => true,
		repository: SketchEntityReferenceRepositoryWriteScope = this.dependencies.repository,
		transaction?: SketchEntityReferenceRebuildTransactionPort,
	): Promise<TopologyBoundaryReferenceGroupReconcileResult> {
		if (!isInputCurrent()) {
			return this.createEmptyResult();
		}
		const plainReferences: SketchEntityReferenceRecord[] = [];
		const faceGroups = new Map<string, SketchEntityReferenceRecord[]>();

		references.forEach((reference) => {
			const faceSource = this.resolveFaceSource(reference);
			if (!faceSource || reference.sourceDescriptor.kind !== 'topology-edge') {
				plainReferences.push(structuredClone(reference));
				return;
			}
			const groupKey = `${reference.targetSketchId}::${reference.selectionIntent!.intentId}`;
			const group = faceGroups.get(groupKey) ?? [];
			group.push(structuredClone(reference));
			faceGroups.set(groupKey, group);
		});

		const nextReferences = [...plainReferences];
		const createdReferenceIds: string[] = [];
		const removedReferenceIds: string[] = [];
		const failedReferenceIds: string[] = [];
		const replacementReferenceIds: string[] = [];
		const targetChanges: TopologyBoundaryReferenceGroupReconcileResult['targetChanges'] = [];
		const diagnostics: ExecutionDiagnostic[] = [];

		for (const group of faceGroups.values()) {
			if (!isInputCurrent()) {
				return this.createEmptyResult();
			}
			try {
				const result = await this.reconcileGroup(
					group,
					definitionChangeOwnerId,
					isInputCurrent,
					repository,
					transaction,
				);
				if (!isInputCurrent()) {
					return this.createEmptyResult();
				}
				nextReferences.push(...result.references);
				createdReferenceIds.push(...result.createdReferenceIds);
				removedReferenceIds.push(...result.removedReferenceIds);
				failedReferenceIds.push(...result.failedReferenceIds);
				replacementReferenceIds.push(...result.replacementReferenceIds);
				targetChanges.push(...result.targetChanges);
				diagnostics.push(...result.diagnostics);
			} catch (error) {
				if (!isInputCurrent()) {
					return this.createEmptyResult();
				}
				const template = group[0];
				const rollbackDiagnostics = transaction && template?.selectionIntent
					? await transaction.rollbackBatch(
						createSketchEntityReferenceIntentBatchKey(
							template.targetSketchId,
							template.selectionIntent.intentId,
						),
					)
					: [];
				const preservedResult = await this.markFaceGroupFailed(
					group,
					`面级转换实体成员协调异常：${
						error instanceof Error ? error.message : String(error)
					}`,
					isInputCurrent,
					repository,
				);
				nextReferences.push(...preservedResult.references);
				failedReferenceIds.push(...preservedResult.failedReferenceIds);
				diagnostics.push(...rollbackDiagnostics);
				diagnostics.push(...preservedResult.diagnostics);
			}
		}

		return {
			references: nextReferences,
			createdReferenceIds,
			removedReferenceIds,
			failedReferenceIds,
			replacementReferenceIds: [...new Set(replacementReferenceIds)].sort(),
			targetChanges,
			diagnostics,
		};
	}

	/**
	 * 协调一个目标草图中的单个面来源组。
	 *
	 * @param group 同一 target sketch、同一 face intent 的成员。
	 * @param definitionChangeOwnerId 阶段 B 传播 ID。
	 * @param isInputCurrent 当前 checkpoint 是否仍拥有正式状态提交权。
	 * @returns 单组协调结果。
	 */
	private async reconcileGroup(
		group: readonly SketchEntityReferenceRecord[],
		definitionChangeOwnerId: string,
		isInputCurrent: () => boolean,
		repository: SketchEntityReferenceRepositoryWriteScope,
		transaction?: SketchEntityReferenceRebuildTransactionPort,
	): Promise<TopologyBoundaryReferenceGroupReconcileResult> {
		if (!isInputCurrent()) {
			return this.createEmptyResult();
		}
		const template = group[0];
		const faceSource = template ? this.resolveFaceSource(template) : null;
		if (!template || !faceSource || template.sourceDescriptor.kind !== 'topology-edge') {
			return this.createUnchangedResult(group);
		}
		const mutationBatchKey = createSketchEntityReferenceIntentBatchKey(
			template.targetSketchId,
			template.selectionIntent!.intentId,
		);

		const faceRegionSources = this.resolveFaceRegionSources(template);
		const boundaryResults: TopologyFaceBoundaryRuntimeResult[] = [];
		for (const regionFaceSource of faceRegionSources) {
			const sourceKey = resolveTopologyFaceReferenceSourceKey(regionFaceSource);
			const sourceReferences = group.filter((reference) => {
				const referenceFaceSource = this.resolveFaceSource(reference);
				return referenceFaceSource
					? resolveTopologyFaceReferenceSourceKey(referenceFaceSource) === sourceKey
					: false;
			});
			const boundaryResult = await this.faceBoundaryRuntimeService.resolve(
				regionFaceSource,
				template.selectionIntent?.intentId,
				this.createBoundaryMemberEvidence(sourceReferences),
				template.selectionIntent?.intentKind === 'topology-loop'
					? 'all-wires'
					: 'best-effort',
			);
			if (!isInputCurrent()) {
				return this.createEmptyResult();
			}
			if (!boundaryResult) {
				return this.markFaceGroupDangling(
					group,
					regionFaceSource,
					'当前历史位置无法安全重定位来源面区域或读取其 Wire 边界。',
					isInputCurrent,
					repository,
				);
			}
			boundaryResults.push(boundaryResult);
		}

		const primaryBoundaryResult = boundaryResults[0];
		if (!primaryBoundaryResult) {
			return this.createUnchangedResult(group);
		}
		const currentBoundaryMembers = template.selectionIntent?.intentKind === 'topology-loop'
			? (() => {
				const selectedWire = this.loopWireMatcher.match(
					group.filter(reference => (
						reference.sourceDescriptor.kind === 'topology-edge'
					)),
					primaryBoundaryResult.innerWires,
				);
				if (selectedWire.status !== 'matched') {
					return selectedWire;
				}
				return {
					status: 'matched' as const,
					members: selectedWire.wire.edgeCandidates.map(edge => ({
						faceSource: primaryBoundaryResult.faceSource,
						edge,
					})),
				};
			})()
			: {
				status: 'matched' as const,
				members: this.faceRegionBoundaryResolver.resolve({
					faces: boundaryResults.map((boundaryResult) => ({
						face: boundaryResult.faceSource,
						ownerKey: resolveTopologySourceMemberScopeKey(
							boundaryResult.faceSource.owner,
						),
						edges: boundaryResult.outerWire.edgeSources.map((source, index) => ({
							key: resolveExternalGeometrySourceDescriptorKey(source),
							edge: boundaryResult.outerWire.edgeCandidates[index]!,
						})),
					})),
				}).regions.flatMap(region => region.boundaryEdges.map(member => ({
					faceSource: member.face,
					edge: member.edge,
				}))),
			};
		if (currentBoundaryMembers.status !== 'matched') {
			// 环只允许回到创建时指定的直接 InnerWire；缺失或歧义时禁止按序号猜测。
			return this.createDanglingLoopResult(
				group,
				currentBoundaryMembers.status,
				isInputCurrent,
				repository,
			);
		}

		const currentFaceRegionSources = boundaryResults.map(result => result.faceSource);
		const currentBoundaryEdges = currentBoundaryMembers.members.map(member => member.edge);
		const matchedActiveReferences: SketchEntityReferenceRecord[] = [];
		const detachedReferenceUpdates: SketchEntityReferenceRecord[] = [];
		const obsoleteActiveReferences: SketchEntityReferenceRecord[] = [];
		const failedActiveReferences = new Map<string, SketchEntityReferenceRecord>();
		const previousMembers = group.filter(reference => (
			reference.sourceDescriptor.kind === 'topology-edge'
		));
		const currentMembers = currentBoundaryMembers.members.map((member, currentIndex) => ({
			source: this.edgeDescriptorFactory.create({
				owner: member.faceSource.owner,
				originSelectionIntentId: template.selectionIntent!.intentId,
				edge: member.edge,
			}),
			currentIndex,
			endpointAnchorRefs: member.edge.endpointAnchorRefs,
		}));
		const correspondence = this.memberCorrespondenceResolver.resolve({
			previous: previousMembers,
			current: currentMembers,
		});
		const targetEndpointTopologyPlan = this.createTargetEndpointTopologyPlan(
			correspondence.matches,
		);
		const replacementReferenceIds = [...new Set([
			...this.targetEndpointCanonicalizer.resolveUnsafeInPlaceReferenceIds(
				targetEndpointTopologyPlan.assignments,
			),
			...targetEndpointTopologyPlan.uncertainSharedReferenceIds,
		])].sort();
		/*
		 * 成员对应已经在纯模块中完成循环起点、方向和数量变化的统一裁决。
		 * 这里按一对一结果刷新 active/detached 状态，不再逐条依赖漂移的物理边 ID。
		 */
		correspondence.matches.forEach(({reference, current}) => {
			const currentMember = currentBoundaryMembers.members[current.currentIndex];
			if (!currentMember) {
				throw new Error(`面边界对应指向不存在的当前成员: ${current.currentIndex}`);
			}
			this.consumeMatchedMember({
				reference,
				edge: currentMember.edge,
				currentFaceSource: currentMember.faceSource,
				currentFaceRegionSources,
				boundaryEdges: currentBoundaryEdges,
				matchedActiveReferences,
				detachedReferenceUpdates,
			});
		});

		correspondence.removed.forEach((reference) => {
			if (resolveSketchEntityReferenceBoundaryMemberState(reference) !== 'detached') {
				obsoleteActiveReferences.push(reference);
			}
		});


		const diagnostics: ExecutionDiagnostic[] = [];
		if (detachedReferenceUpdates.length > 0) {
			await executeSketchEntityReferenceRepositoryWrite(
				this.dependencies.repository,
				repository,
				isInputCurrent,
				new Error('checkpoint 面边界协调已过期'),
				async tx => {
					await tx.saveReferences(detachedReferenceUpdates);
				},
			);
		}
		if (!isInputCurrent()) {
			return this.createEmptyResult();
		}

		const creatableItems = correspondence.inserted
			.map((current) => {
				const member = currentBoundaryMembers.members[current.currentIndex];
				if (!member) {
					throw new Error(`面边界新增计划指向不存在的当前成员: ${current.currentIndex}`);
				}
				return {
					edge: member.edge,
					faceSource: member.faceSource,
					descriptor: current.source,
					currentIndex: current.currentIndex,
				};
			})
			.filter((item) => {
				const {descriptor} = item;
				if (descriptor.locator.resolutionLevel !== 'unsupported') {
					return true;
				}
				diagnostics.push(createExecutionDiagnostic({
					kind: 'unsupported-curve-type',
					level: 'warning',
					message: `面边界新增边暂不支持转换，已保留其他面成员: edgeIndex=${descriptor.locator.edgeIndex}`,
					sourceRefEntityId: descriptor.locator.subEntityId,
				}));
				return false;
			});
		const creatableDescriptors = creatableItems.map(item => item.descriptor);
		let createdReferences: SketchEntityReferenceRecord[] = [];
		let creationCoverageComplete =
			creatableDescriptors.length === correspondence.inserted.length;
		if (creatableDescriptors.length > 0) {
			const sourceDisplayLabelsByKey: Record<string, string> = {};
			const selectionIntentsBySourceKey: Record<string, NonNullable<SketchEntityReferenceRecord['selectionIntent']>> = {};
			creatableItems.forEach(({
				descriptor,
				faceSource: edgeFaceSource,
				currentIndex,
			}) => {
				const sourceKey = resolveExternalGeometrySourceDescriptorKey(descriptor);
				sourceDisplayLabelsByKey[sourceKey] = `边线${descriptor.locator.edgeIndex + 1}`;
				selectionIntentsBySourceKey[sourceKey] = {
					...template.selectionIntent!,
					sourceIndex: currentIndex,
					sourceCount: currentBoundaryEdges.length,
					topologyFaceSource: edgeFaceSource,
					topologyFaceRegionSources: currentFaceRegionSources,
				};
			});
			const templateTarget = this.dependencies.runtime.getSketchObject(
				template.targetSketchId,
				template.targetEntityId,
			);
			const createInput = {
				targetSketchId: template.targetSketchId,
				sources: creatableDescriptors,
				sourceDisplayLabelsByKey,
				selectionIntentsBySourceKey,
				options: {
					asConstruction: Boolean(
						templateTarget
						&& Reflect.get(templateTarget, 'isConstruction') === true,
					),
				},
			};
			const createResult = transaction
				? await this.dependencies.createService.executeDerived(
					createInput,
					definitionChangeOwnerId,
					isInputCurrent,
					repository,
					transaction,
					mutationBatchKey,
				)
				: await this.dependencies.createService.executeDerived(
					createInput,
					definitionChangeOwnerId,
					isInputCurrent,
				);
			if (!isInputCurrent()) {
				return this.createEmptyResult();
			}
			createdReferences.push(...createResult.createdReferences);
			diagnostics.push(...createResult.diagnostics);
			creationCoverageComplete = creationCoverageComplete
				&& createdReferences.length === creatableDescriptors.length
				&& !createResult.diagnostics.some((diagnostic) => diagnostic.level === 'error');
		}

		if (!creationCoverageComplete && createdReferences.length > 0 && !transaction) {
			/*
			 * executeDerived 正常应保持整批原子性；这里仍对异常的部分成功结果做补偿，
			 * 防止新旧成员同时留在正式草图中形成不可追踪的半完成面边界。
			 */
			const compensationResult = await this.compensateCreatedReferences(
				createdReferences,
				definitionChangeOwnerId,
				isInputCurrent,
				repository,
			);
			if (!isInputCurrent()) {
				return this.createEmptyResult();
			}
			createdReferences = compensationResult[0];
			diagnostics.push(...compensationResult[1]);
		}

		/*
		 * 只有当前面新成员已经完整落地，才能清理合并后被淘汰的旧成员。
		 * 创建失败时回滚本轮成员变化并把 Face 批次标记为 failed，避免正式草图只剩部分边界。
		 */
		let removedActiveReferenceIds: string[] = [];
		if (obsoleteActiveReferences.length > 0 && creationCoverageComplete) {
			const removedReferenceIds = obsoleteActiveReferences.map(
				reference => reference.referenceId,
			);
			const removeResult = transaction
				? await this.dependencies.removeService.removeDerivedReferencesWithTargets(
					removedReferenceIds,
					definitionChangeOwnerId,
					isInputCurrent,
					repository,
					transaction,
					mutationBatchKey,
				)
				: await this.dependencies.removeService.removeDerivedReferencesWithTargets(
					removedReferenceIds,
					definitionChangeOwnerId,
					isInputCurrent,
				);
			if (!isInputCurrent()) {
				return this.createEmptyResult();
			}
			removedActiveReferenceIds = [...removeResult.removedReferenceIds];
			const removedReferenceIdSet = new Set(removedActiveReferenceIds);
			obsoleteActiveReferences.forEach((reference) => {
				if (!removedReferenceIdSet.has(reference.referenceId)) {
					failedActiveReferences.set(reference.referenceId, reference);
				}
			});
			diagnostics.push(...removeResult.diagnostics);
			const removalCoverageComplete = (
				obsoleteActiveReferences.every(reference => (
					removedReferenceIdSet.has(reference.referenceId)
				))
				&& !removeResult.diagnostics.some(diagnostic => diagnostic.level === 'error')
			);
			if (!removalCoverageComplete && createdReferences.length > 0 && !transaction) {
				/*
				 * 新成员已经落地但旧集合未完整退出时，必须反向删除本轮新成员。
				 * 这样 failed 状态只保留旧投影，不产生两套同时 active 的边界。
				 */
				const compensationResult = await this.compensateCreatedReferences(
					createdReferences,
					definitionChangeOwnerId,
					isInputCurrent,
					repository,
				);
				if (!isInputCurrent()) {
					return this.createEmptyResult();
				}
				createdReferences = compensationResult[0];
				diagnostics.push(...compensationResult[1]);
			}
		} else if (!creationCoverageComplete) {
			obsoleteActiveReferences.forEach((reference) => {
				failedActiveReferences.set(reference.referenceId, reference);
			});
		}
		const memberUpdateFailed = !creationCoverageComplete || failedActiveReferences.size > 0;
		if (memberUpdateFailed) {
			group.forEach((reference) => {
				if (resolveSketchEntityReferenceBoundaryMemberState(reference) !== 'detached') {
					failedActiveReferences.set(reference.referenceId, reference);
				}
			});
			if (transaction) {
				/*
				 * Face/Loop 成员属于同一选择意图批次；部分创建或删除失败时撤销该批全部
				 * runtime/repository before-image。临时新成员不经过真实删除入口，因此也
				 * 不会登记提交后的 sketch-entity-removed 事件。
				 */
				diagnostics.push(...await transaction.rollbackBatch(mutationBatchKey));
				createdReferences = [];
				removedActiveReferenceIds = [];
			} else {
				/*
				 * 无事务兼容调用无法再恢复补偿失败后仍存活的半成品；这些记录必须继续保留为
				 * failed 事实并通过结果显式报告，禁止伪装成已完整回滚的空结果。
				 */
				createdReferences.forEach((reference) => {
					failedActiveReferences.set(reference.referenceId, reference);
				});
			}
		}

		const failedReferenceIds = [...failedActiveReferences.keys()].sort();
		const memberUpdateFailureReason = [...diagnostics]
			.reverse()
			.find(diagnostic => diagnostic.level === 'error')
			?.message;
		const failureDispositionMessage = transaction
			? '已回滚本轮变化并保留上一次有效投影'
			: createdReferences.length > 0
				? '补偿未能完整清理半成品，残留成员已保留并标记为 failed'
				: '已补偿本轮变化并保留上一次有效投影';
		const failedEntries = [...failedActiveReferences.values()].map((reference) => ({
			reference,
			diagnostic: createExecutionDiagnostic({
				kind: 'rebuild-failed',
				level: 'error',
				message: `面边界成员更新未能完整提交，${failureDispositionMessage}${
					memberUpdateFailureReason ? `：${memberUpdateFailureReason}` : ''
				}`,
				referenceId: reference.referenceId,
				targetSketchId: reference.targetSketchId,
				targetEntityId: reference.targetEntityId,
			}),
		}));
		if (failedEntries.length > 0) {
			await executeSketchEntityReferenceRepositoryWrite(
				this.dependencies.repository,
				repository,
				isInputCurrent,
				new Error('checkpoint 面边界协调已过期'),
				async tx => {
					for (const entry of failedEntries) {
						await tx.updateReferenceStatus(
							entry.reference.referenceId,
							'failed',
							entry.diagnostic,
						);
					}
				},
			);
			if (!isInputCurrent()) {
				return this.createEmptyResult();
			}
			diagnostics.push(...failedEntries.map(entry => entry.diagnostic));
		}

		if (memberUpdateFailed) {
			return {
				references: [],
				createdReferenceIds: createdReferences.map(reference => reference.referenceId),
				removedReferenceIds: removedActiveReferenceIds,
				failedReferenceIds,
				replacementReferenceIds: [],
				targetChanges: [],
				diagnostics,
			};
		}

		return {
			references: [
				...matchedActiveReferences,
				...createdReferences,
			],
			createdReferenceIds: createdReferences.map((reference) => reference.referenceId),
			removedReferenceIds: removedActiveReferenceIds,
			failedReferenceIds,
			replacementReferenceIds,
			targetChanges: obsoleteActiveReferences
				.filter((reference) => removedActiveReferenceIds.includes(reference.referenceId))
				.map((reference) => ({
					referenceId: reference.referenceId,
					targetSketchId: reference.targetSketchId,
					oldTargetEntityId: reference.targetEntityId,
					newTargetEntityId: reference.targetEntityId,
					changeKind: 'in-place',
				})),
			diagnostics,
		};
	}

	/**
	 * 反向清理本轮已创建但不能提交的面边界成员。
	 *
	 * @param references 本轮派生创建的成员。
	 * @param definitionChangeOwnerId 当前 checkpoint 传播 ID。
	 * @param isInputCurrent 当前 checkpoint 是否仍拥有正式状态提交权。
	 * @returns 尚未补偿删除的成员与补偿诊断。
	 */
	private async compensateCreatedReferences(
		references: readonly SketchEntityReferenceRecord[],
		definitionChangeOwnerId: string,
		isInputCurrent: () => boolean,
		repository: SketchEntityReferenceRepositoryWriteScope,
	): Promise<[SketchEntityReferenceRecord[], ExecutionDiagnostic[]]> {
		const referenceIds = references.map(reference => reference.referenceId);
		const compensationResult = await this.dependencies.removeService
			.removeDerivedReferencesWithTargets(
				referenceIds,
				definitionChangeOwnerId,
				isInputCurrent,
				repository,
			);
		const compensatedReferenceIdSet = new Set(
			compensationResult.removedReferenceIds,
		);
		return [
			references.filter(reference => (
				!compensatedReferenceIdSet.has(reference.referenceId)
			)),
			[...compensationResult.diagnostics],
		];
	}

	/**
	 * 将成员匹配结果转换成“当前来源 vertex → 旧目标点”的完整归属证据。
	 *
	 * 这里必须在任何单边原位几何写入之前读取旧目标点拓扑；一旦两个旧边共享的点被连续写入，
	 * 后写入的边会覆盖前一条边的位置，届时已无法判断该点原本服务于哪些目标端点。
	 *
	 * @param matches 旧面成员到当前 Wire 成员的一对一匹配。
	 * @returns 方向已确认的端点归属，以及未知方向下需要迁移的共享点引用。
	 */
	private createTargetEndpointTopologyPlan(
		matches: readonly TopologyBoundaryMemberMatch[],
	): SketchReferenceTargetEndpointTopologyPlan {
		// 第一阶段只读取旧目标点，不做任何几何写入，冻结本批共享点拓扑。
		const endpointMembers = matches.flatMap(({reference, current, evidence}) => {
			if (resolveSketchEntityReferenceBoundaryMemberState(reference) === 'detached') {
				return [];
			}
			const targetEntity = this.dependencies.runtime.getSketchObject(
				reference.targetSketchId,
				reference.targetEntityId,
			);
			const targetEndpoints = targetEntity
				? this.targetAnchorBindingCollector.resolveTargetEndpointBindings(targetEntity)
				: null;
			if (!targetEndpoints) {
				return [];
			}
			return [{reference, current, evidence, targetEndpoints}];
		});

		// 第二阶段标记被多条旧引用共同持有的目标点，未知方向只在这里触发保守迁移。
		const referenceIdsByTargetPointId = new Map<string, Set<string>>();
		endpointMembers.forEach(({reference, targetEndpoints}) => {
			[targetEndpoints.startPoint, targetEndpoints.endPoint].forEach((targetPoint) => {
				const referenceIds = referenceIdsByTargetPointId.get(targetPoint.uuid)
					?? new Set<string>();
				referenceIds.add(reference.referenceId);
				referenceIdsByTargetPointId.set(targetPoint.uuid, referenceIds);
			});
		});

		const assignments: SketchReferenceTargetEndpointTopologyAssignment[] = [];
		const uncertainSharedReferenceIds = new Set<string>();
		endpointMembers.forEach(({reference, current, evidence, targetEndpoints}) => {
			const endpointAnchorRefs = current.endpointAnchorRefs;
			const anchorEvidence = this.endpointAnchorEvidenceResolver.resolve(
				reference,
				current,
			);
			if (anchorEvidence.direction === 'unknown') {
				const touchesSharedPoint = [
					targetEndpoints.startPoint,
					targetEndpoints.endPoint,
				].some(targetPoint => (
					(referenceIdsByTargetPointId.get(targetPoint.uuid)?.size ?? 0) > 1
				));
				if (touchesSharedPoint) {
					uncertainSharedReferenceIds.add(reference.referenceId);
				}
				return;
			}
			if (!endpointAnchorRefs) {
				return;
			}

			// 第三阶段只为方向已证明的成员生成“来源 vertex → 旧目标点”归属。
			const isReversed = anchorEvidence.direction === 'reverse';
			const startAnchorRef = isReversed
				? endpointAnchorRefs.end
				: endpointAnchorRefs.start;
			const endAnchorRef = isReversed
				? endpointAnchorRefs.start
				: endpointAnchorRefs.end;
			if (startAnchorRef) {
				assignments.push({
					referenceId: reference.referenceId,
					sourceAnchorId: resolveTopologyEndpointAnchorId(
						current.source.owner,
						startAnchorRef,
					),
					targetPoint: targetEndpoints.startPoint,
					evidence,
				});
			}
			if (endAnchorRef) {
				assignments.push({
					referenceId: reference.referenceId,
					sourceAnchorId: resolveTopologyEndpointAnchorId(
						current.source.owner,
						endAnchorRef,
					),
					targetPoint: targetEndpoints.endPoint,
					evidence,
				});
			}
		});
		return {
			assignments,
			uncertainSharedReferenceIds: [...uncertainSharedReferenceIds].sort(),
		};
	}

	/**
	 * 消费一组已经由面集合协调确认的旧成员与当前边。
	 *
	 * @param input 旧成员、当前边、当前面身份与结果集合。
	 */
	private consumeMatchedMember(input: {
		reference: SketchEntityReferenceRecord;
		edge: TopologyEdgeRelocationCandidate;
		currentFaceSource: TopologyFaceReferenceSourceDescriptor;
		currentFaceRegionSources: readonly TopologyFaceReferenceSourceDescriptor[];
		boundaryEdges: readonly TopologyEdgeRelocationCandidate[];
		matchedActiveReferences: SketchEntityReferenceRecord[];
		detachedReferenceUpdates: SketchEntityReferenceRecord[];
	}): void {
		const refreshedReference: SketchEntityReferenceRecord = {
			...input.reference,
			sourceDescriptor: this.edgeDescriptorFactory.create({
				owner: input.currentFaceSource.owner,
				originSelectionIntentId: input.reference.selectionIntent!.intentId,
				edge: input.edge,
			}),
			selectionIntent: {
				...input.reference.selectionIntent!,
				sourceIndex: input.boundaryEdges.indexOf(input.edge),
				sourceCount: input.boundaryEdges.length,
				topologyFaceSource: input.currentFaceSource,
				topologyFaceRegionSources: input.currentFaceRegionSources,
			},
		};
		if (resolveSketchEntityReferenceBoundaryMemberState(input.reference) === 'detached') {
			// detached 只刷新来源身份，不更新目标，也不重新创建被用户解除的成员。
			input.detachedReferenceUpdates.push(refreshedReference);
			return;
		}
		input.matchedActiveReferences.push(refreshedReference);
	}

	/**
	 * 从引用记录读取完整面来源。
	 *
	 * @param reference 引用记录。
	 * @returns 新版 topology face 来源；旧记录返回 null。
	 */
	private resolveFaceSource(
		reference: SketchEntityReferenceRecord,
	): TopologyFaceReferenceSourceDescriptor | null {
		return (
			reference.selectionIntent?.intentKind === 'topology-face'
			|| reference.selectionIntent?.intentKind === 'topology-loop'
		)
			? reference.selectionIntent.topologyFaceSource ?? null
			: null;
	}

	/**
	 * 读取单面或相邻面区域的完整持久来源集合。
	 *
	 * @param reference 面组中的任一引用记录。
	 * @returns 去重后的区域面来源；旧记录回退为单个 topologyFaceSource。
	 */
	private resolveFaceRegionSources(
		reference: SketchEntityReferenceRecord,
	): TopologyFaceReferenceSourceDescriptor[] {
		const regionSources = reference.selectionIntent?.topologyFaceRegionSources;
		const fallbackSource = this.resolveFaceSource(reference);
		const sourceCandidates = regionSources && regionSources.length > 0
			? regionSources
			: fallbackSource
				? [fallbackSource]
				: [];
		const sourceByKey = new Map<string, TopologyFaceReferenceSourceDescriptor>();
		sourceCandidates.forEach((source) => {
			sourceByKey.set(
				resolveTopologyFaceReferenceSourceKey(source),
				structuredClone(source),
			);
		});
		return [...sourceByKey.values()];
	}

	/**
	 * 从面组成员提取当前持久化边界身份依据。
	 *
	 * 该依据只交给受约束面兼容，不单独用于按 index 定位面。
	 *
	 * @param group 同一面选择意图的引用成员。
	 * @returns 去重并按成员来源顺序稳定排列的边界依据。
	 */
	private createBoundaryMemberEvidence(
		group: readonly SketchEntityReferenceRecord[],
	): TopologyFaceBoundaryMemberEvidence[] {
		const evidenceByKey = new Map<string, TopologyFaceBoundaryMemberEvidence>();
		group.forEach((reference) => {
			if (reference.sourceDescriptor.kind !== 'topology-edge') {
				return;
			}
			const locator = reference.sourceDescriptor.locator;
			const key = locator.persistentRef
				?? locator.subEntityId
				?? `edge-index:${locator.edgeIndex}`;
			if (!evidenceByKey.has(key)) {
				evidenceByKey.set(key, {
					edgeIndex: locator.edgeIndex,
					persistentRef: locator.persistentRef,
					subEntityId: locator.subEntityId,
					curveKind: locator.curveKind,
				});
			}
		});
		return [...evidenceByKey.values()];
	}

	/**
	 * 创建不改变成员集合的结果。
	 *
	 * @param references 原组记录。
	 * @returns 独立副本结果。
	 */
	private createUnchangedResult(
		references: readonly SketchEntityReferenceRecord[],
	): TopologyBoundaryReferenceGroupReconcileResult {
		return {
			references: references
				.filter(isActiveSketchEntityReferenceRecord)
				.map((reference) => structuredClone(reference)),
			createdReferenceIds: [],
			removedReferenceIds: [],
			failedReferenceIds: [],
			replacementReferenceIds: [],
			targetChanges: [],
			diagnostics: [],
		};
	}

	/**
	 * 保留面引用目标，并把无法继续重定位的未解除成员标记为悬空。
	 *
	 * 仓库状态是实体与草图 warning 的唯一事实来源；这里只改变引用诊断状态，
	 * 不删除目标实体，也不把该组继续交给单边重建器猜测其它来源。
	 *
	 * @param references 同一面选择意图的成员记录。
	 * @param faceSource 无法重定位的权威面来源。
	 * @param reason 当前失败原因。
	 * @param isInputCurrent 当前 checkpoint 是否仍拥有正式状态提交权。
	 * @returns 不再参与本轮单边重建的面组结果。
	 */
	private async markFaceGroupDangling(
		references: readonly SketchEntityReferenceRecord[],
		faceSource: TopologyFaceReferenceSourceDescriptor,
		reason: string,
		isInputCurrent: () => boolean,
		repository: SketchEntityReferenceRepositoryWriteScope,
	): Promise<TopologyBoundaryReferenceGroupReconcileResult> {
		const retainedReferences = references.filter((reference) => (
			isRetainedSketchEntityReferenceRecord(reference)
				&& resolveSketchEntityReferenceBoundaryMemberState(reference) !== 'detached'
		));
		const diagnostics = retainedReferences.map((reference) => createExecutionDiagnostic({
				kind: 'source-missing',
				level: 'warning',
				message: `来源面无法重定位，已保留转换实体并标记为 dangling：${reason}`,
				referenceId: reference.referenceId,
				sourceRefEntityId: faceSource.locator.semanticName
					?? faceSource.locator.persistentRef
					?? faceSource.locator.subEntityId,
				targetSketchId: reference.targetSketchId,
				targetEntityId: reference.targetEntityId,
			}));
		if (diagnostics.length > 0) {
			await executeSketchEntityReferenceRepositoryWrite(
				this.dependencies.repository,
				repository,
				isInputCurrent,
				new Error('checkpoint 面边界协调已过期'),
				async tx => {
					for (let index = 0; index < retainedReferences.length; index += 1) {
						const reference = retainedReferences[index];
						const diagnostic = diagnostics[index];
						if (reference && diagnostic) {
							await tx.updateReferenceStatus(
								reference.referenceId,
								'dangling',
								diagnostic,
							);
						}
					}
				},
			);
		}
		return {
			references: [],
			createdReferenceIds: [],
			removedReferenceIds: [],
			failedReferenceIds: retainedReferences.map(reference => reference.referenceId),
			replacementReferenceIds: [],
			targetChanges: [],
			diagnostics,
		};
	}

	/**
	 * 指定 InnerWire 消失或匹配歧义时，把整组活动成员标记为 dangling。
	 *
	 * 环组不能退回普通单边重建，否则单边索引兜底可能把成员错误绑定到其它 Wire。
	 *
	 * @param references 当前环组记录。
	 * @param status Wire 匹配失败类别。
	 * @param isInputCurrent 当前 checkpoint 是否仍拥有正式状态提交权。
	 * @returns 不再进入本轮单边重建的失败结果。
	 */
	private async createDanglingLoopResult(
		references: readonly SketchEntityReferenceRecord[],
		status: 'missing' | 'ambiguous',
		isInputCurrent: () => boolean,
		repository: SketchEntityReferenceRepositoryWriteScope,
	): Promise<TopologyBoundaryReferenceGroupReconcileResult> {
		const failedReferences = references.filter(reference => (
			resolveSketchEntityReferenceBoundaryMemberState(reference) !== 'detached'
		));
		const failedEntries = failedReferences.map((reference) => ({
			reference,
			diagnostic: createExecutionDiagnostic({
				kind: status === 'missing' ? 'source-missing' : 'source-ambiguous',
				level: 'warning',
				message: status === 'missing'
					? `转换实体引用的原内环已消失，已保留目标并标记 dangling: ${reference.referenceId}`
					: `转换实体引用的原内环匹配存在歧义，已禁止猜测并标记 dangling: ${reference.referenceId}`,
				referenceId: reference.referenceId,
				targetSketchId: reference.targetSketchId,
				targetEntityId: reference.targetEntityId,
			}),
		}));
		if (failedEntries.length > 0) {
			await executeSketchEntityReferenceRepositoryWrite(
				this.dependencies.repository,
				repository,
				isInputCurrent,
				new Error('checkpoint 面边界协调已过期'),
				async tx => {
					for (const entry of failedEntries) {
						await tx.updateReferenceStatus(
							entry.reference.referenceId,
							'dangling',
							entry.diagnostic,
						);
					}
				},
			);
		}
		return {
			references: [],
			createdReferenceIds: [],
			removedReferenceIds: [],
			failedReferenceIds: failedReferences
				.map(reference => reference.referenceId)
				.sort(),
			replacementReferenceIds: [],
			targetChanges: [],
			diagnostics: failedEntries.map(entry => entry.diagnostic),
		};
	}

	/**
	 * 保留上一版面投影，并把成员协调事务异常标记为计算失败。
	 *
	 * 来源面仍然存在时不能借用 dangling 语义；这里仅关闭本轮 Face 批次，
	 * 让诊断投影链显示 failed，同时保留后续重新计算恢复 active 的入口。
	 *
	 * @param references 同一面选择意图的成员记录。
	 * @param reason 本轮成员协调失败原因。
	 * @param isInputCurrent 当前 checkpoint 是否仍拥有正式状态提交权。
	 * @param repository 当前调用使用的仓储写作用域。
	 * @returns 不再参与本轮单边重建的失败结果。
	 */
	private async markFaceGroupFailed(
		references: readonly SketchEntityReferenceRecord[],
		reason: string,
		isInputCurrent: () => boolean,
		repository: SketchEntityReferenceRepositoryWriteScope,
	): Promise<TopologyBoundaryReferenceGroupReconcileResult> {
		const failedReferences = references.filter((reference) => (
			isRetainedSketchEntityReferenceRecord(reference)
				&& resolveSketchEntityReferenceBoundaryMemberState(reference) !== 'detached'
		));
		const failedEntries = failedReferences.map((reference) => ({
			reference,
			diagnostic: createExecutionDiagnostic({
				kind: 'rebuild-failed',
				level: 'error',
				message: `来源面仍存在，但边界成员更新失败；已保留上一次有效投影：${reason}`,
				referenceId: reference.referenceId,
				targetSketchId: reference.targetSketchId,
				targetEntityId: reference.targetEntityId,
			}),
		}));
		if (failedEntries.length > 0) {
			await executeSketchEntityReferenceRepositoryWrite(
				this.dependencies.repository,
				repository,
				isInputCurrent,
				new Error('checkpoint 面边界协调已过期'),
				async tx => {
					for (const entry of failedEntries) {
						await tx.updateReferenceStatus(
							entry.reference.referenceId,
							'failed',
							entry.diagnostic,
						);
					}
				},
			);
		}
		return {
			references: [],
			createdReferenceIds: [],
			removedReferenceIds: [],
			failedReferenceIds: failedReferences.map(reference => reference.referenceId),
			replacementReferenceIds: [],
			targetChanges: [],
			diagnostics: failedEntries.map(entry => entry.diagnostic),
		};
	}

	/**
	 * 创建 checkpoint 已过期时的无副作用结果。
	 *
	 * @returns 不包含任何成员或诊断变化的结果。
	 */
	private createEmptyResult(): TopologyBoundaryReferenceGroupReconcileResult {
		return {
			references: [],
			createdReferenceIds: [],
			removedReferenceIds: [],
			failedReferenceIds: [],
			replacementReferenceIds: [],
			targetChanges: [],
			diagnostics: [],
		};
	}
}
